/**
 * Lenis smooth scrolling, driven by GSAP's ticker so it stays in sync with ScrollTrigger.
 * Also owns in-page anchor scrolling (with a header offset), which falls back to native
 * smooth scrolling when Lenis is off (reduced motion).
 */
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

let lenis = null;

export function initLenis() {
  lenis = new Lenis({ lerp: 0.1, smoothWheel: true, wheelMultiplier: 1 });
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
  return lenis;
}

export const getLenis = () => lenis;

export function stopScroll() {
  if (lenis) lenis.stop();
  document.body.classList.add('is-locked');
}

export function startScroll() {
  if (lenis) lenis.start();
  document.body.classList.remove('is-locked');
}

const headerOffset = () => -(document.querySelector('[data-header]')?.offsetHeight || 0) - 12;

/** Scrolls to an element and moves keyboard focus there without a second jump. */
export function scrollToTarget(target) {
  if (!target) return;
  if (lenis) {
    lenis.scrollTo(target, { offset: headerOffset(), duration: 1.4 });
  } else {
    const y = target.getBoundingClientRect().top + window.scrollY + headerOffset();
    window.scrollTo({ top: y, behavior: document.documentElement.classList.contains('reduced-motion') ? 'auto' : 'smooth' });
  }
  const focusable = target.matches('a, button, input, textarea, select, [tabindex]') ? target : null;
  if (!focusable) target.setAttribute('tabindex', '-1');
  (focusable || target).focus({ preventScroll: true });
}

/** Same-page hash links (#signup, #about, ...). */
export function initAnchors() {
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href*="#"]');
    if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    const url = new URL(link.href, location.href);
    if (url.pathname !== location.pathname || !url.hash) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    history.replaceState(null, '', url.hash);
    scrollToTarget(target);
  });

  // Arriving on a page with a hash (e.g. /contact#signup) after fonts and layout settle.
  if (location.hash.length > 1) {
    const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (target) window.addEventListener('load', () => setTimeout(() => scrollToTarget(target), 60), { once: true });
  }
}
